import DOMPurify from "dompurify";

// Contract text is HTML written by one party and shown to others on the blocking acceptance
// screen, so only document formatting survives: no scripts, forms, inputs, style sheets, images
// or embeds (a remote image would send every reader's IP to a third party). Same allowlist as
// the backend, which sanitizes before storing (ContractHtml.cs).
const TAGS = [
  "p", "br", "hr", "div", "span", "h1", "h2", "h3", "h4", "h5", "h6",
  "strong", "b", "em", "i", "u", "s", "del", "ins", "sub", "sup", "code", "pre", "blockquote",
  "ul", "ol", "li", "a", "table", "thead", "tbody", "tfoot", "tr", "th", "td",
];
const ATTRS = ["href", "target", "rel", "style", "colspan", "rowspan"];
// What the rich-text editor emits; anything else (position, display, url(), ...) is dropped,
// so contract text can't cover or restyle the page around it.
const CSS_PROPERTIES = new Set([
  "text-align", "color", "background-color", "font-size", "font-family", "font-weight",
  "font-style", "text-decoration", "margin-left", "padding-left",
]);

// A separate instance, so these hooks don't change how the rest of the app sanitizes.
const purifier = DOMPurify(window);

purifier.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName !== "style") return;
  const kept = data.attrValue
    .split(";")
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const property = declaration.split(":")[0].trim().toLowerCase();
      return CSS_PROPERTIES.has(property) && !/url\(|expression\(/i.test(declaration);
    });
  data.attrValue = kept.join("; ");
  if (!kept.length) data.keepAttr = false;
});

purifier.addHook("afterSanitizeAttributes", (node) => {
  // Links opening a new tab must not get a handle on the acceptance page.
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") node.setAttribute("rel", "noopener noreferrer");
});

export const sanitizeContractHtml = (html) =>
  purifier.sanitize(html || "", {
    ALLOWED_TAGS: TAGS,
    ALLOWED_ATTR: ATTRS,
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
  });

export default function SafeHtml({ html, className, style }) {
  return <div className={className} style={style} dangerouslySetInnerHTML={{ __html: sanitizeContractHtml(html) }} />;
}
